import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const faqs = [
    {
        question: "Is hypnotherapy the same as meditation?",
        answer: "Not exactly. Meditation often focuses on observation and stillness, while hypnosis is more directed and purposeful. Both can be calming, but they work in different ways."
    },
    {
        question: "Do I need to believe in hypnosis for it to work?",
        answer: "No. You do not need perfect belief. You only need enough willingness to listen and allow the process to unfold."
    },
    {
        question: "What does being hypnotised actually feel like?",
        answer: "For many people, it feels like quiet focus, relaxation, and a sense of being absorbed in the experience without needing to force anything."
    },
    {
        question: "How many sessions does it take to make a difference?",
        answer: "That depends on the person and the issue. Some people notice a shift quickly, while others prefer repeating a recording over time."
    },
    {
        question: "Can I use the recordings if I have never had hypnosis before?",
        answer: "Yes. The recordings are designed to be accessible even if you are completely new to hypnosis."
    },
    {
        question: "How do I listen for best results?",
        answer: "Find a quiet place, use headphones if possible, and give yourself enough time to relax without interruption."
    }
];

const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    return (
        <div className="border-b border-nature-200 last:border-b-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full py-5 flex items-center justify-between text-left hover:text-copper-600 transition-colors"
            >
                <span className="font-heading text-lg text-nature-900 pr-8">{question}</span>
                <ChevronDown className={`w-5 h-5 text-copper-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="pb-5 pr-12">
                    <p className="text-nature-800/70 font-body leading-relaxed">{answer}</p>
                </div>
            )}
        </div>
    );
};

export const IsThisForMe = () => {
    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <SEO 
                title="Is Hypnotherapy For Me? | Hypnosis Mastery Series"
                description="Wondering if hypnotherapy is right for you? An honest look at who hypnotherapy works best for, what it can help with, and what to expect when you begin."
                canonical="https://hypnosismasteryseries.com/is-this-for-me"
            />
            
            {/* Intro */}
            <section className="max-w-3xl mx-auto px-6 py-16">
                <h1 className="font-heading text-4xl sm:text-5xl font-light text-nature-900 leading-tight mb-8 text-center opacity-0 animate-fade-in-up">
                    Is Hypnotherapy <span className="italic">Right for Me?</span>
                </h1>
                
                <div className="space-y-6 text-nature-800/80 font-body leading-relaxed text-lg opacity-0 animate-fade-in-up stagger-1">
                    <p>
                        If you're wondering whether hypnotherapy is a good fit for you, this page is here to help you decide. The Hypnosis Mastery Series is designed for people who want a calm, practical way to work with stress, anxiety, sleep, habits, and emotional change — at their own pace.
                    </p>
                    <p>
                        The recordings may be a good fit if you:
                    </p>
                    <ul className="space-y-3 my-6">
                        {[
                            'Feel stuck in anxious thoughts or overthinking',
                            'Want better sleep or support with insomnia',
                            'Are trying to change a habit or pattern that keeps repeating',
                            'Want to feel calmer, more confident, or more emotionally steady',
                            'Prefer a gentle process you can return to whenever you need it'
                        ].map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <span className="w-2 h-2 rounded-full bg-copper-500 mt-2 flex-shrink-0"></span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* What Kind of Person */}
            <section className="py-16 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-900 mb-8">
                        What kind of person tends to benefit most?
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            The people who usually get the most out of this work are not looking for a dramatic fix. They are often people who are ready for something steadier — a process that feels calm, respectful, and easy to return to over time.
                        </p>
                        <p>
                            That might include people who:
                        </p>
                        <ul className="space-y-3 my-6">
                            {[
                                'Want to reduce the intensity of stress or anxiety',
                                'Need support with sleep or racing thoughts at night',
                                'Are trying to stop repeating a habit they no longer want',
                                'Want to build more confidence or self-trust',
                                'Feel mentally overloaded and want a more grounded way forward'
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="w-2 h-2 rounded-full bg-copper-500 mt-2 flex-shrink-0"></span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-nature-900 font-medium">
                            If that sounds like you, you are probably in the right place.
                        </p>
                    </div>
                </div>
            </section>

            {/* What Challenges */}
            <section className="py-16 bg-nature-50">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-900 mb-8">
                        What kinds of challenges can hypnotherapy help with?
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            The Hypnosis Mastery Series focuses on everyday patterns that many people struggle with quietly: anxiety, sleep problems, habits, low confidence, overthinking, and emotional strain. The aim is not to force change, but to help your mind become more open to a better direction.
                        </p>
                        <p>
                            Common areas people explore through the recordings include:
                        </p>
                        
                        <ul className="space-y-3 my-6">
                            {[
                                'Emotional balance and mental wellbeing',
                                'Habits and behaviours',
                                'Sleep and physical wellbeing',
                                'Confidence, relationships, and personal growth'
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="w-2 h-2 rounded-full bg-copper-500 mt-2 flex-shrink-0"></span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        
                        <p className="text-nature-900 font-medium">
                            If you are unsure whether your situation is a good fit, the simplest test is this: if it is something that involves your thoughts, feelings, habits, sleep, or emotional patterns, there is a good chance you will find a useful recording here.
                        </p>
                    </div>
                </div>
            </section>

            {/* What Hypnotherapy Is Not */}
            <section className="py-16 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-900 mb-8">
                        What hypnotherapy is not
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            It's also helpful to be clear about what hypnotherapy is not. It is not a magic wand, and it is not a way to erase a problem in one moment. It works best when you approach it as a process — one that gives your mind repeated, useful input over time.
                        </p>
                        <p>
                            Hypnotherapy is not:
                        </p>
                        <ul className="space-y-3 my-6">
                            {[
                                'A sleep spell or trance show',
                                'A way to control your mind against your will',
                                'A replacement for medical or psychological care',
                                'A promise that every issue disappears instantly'
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="w-2 h-2 rounded-full bg-copper-500 mt-2 flex-shrink-0"></span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-nature-900 font-medium">
                            It is a focused, guided process designed to support change in a calm and practical way. For many people, that is exactly what they have been looking for.
                        </p>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-16 bg-nature-800">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-50 mb-8">
                        How the Hypnosis Mastery Series works
                    </h2>
                    
                    <div className="space-y-6 text-nature-100/80 font-body leading-relaxed">
                        <p>
                            The recordings are designed to be easy to follow. Each one gives you a clear theme, a quiet structure, and a focused hypnotic process that you can listen to in your own space, on your own time.
                        </p>
                        <p>
                            You can start with the recording that best matches your current concern, or you can begin with the free <Link to="/recording/freedom-from-insomnia" className="text-copper-500 hover:text-copper-400 underline cursor-pointer">Freedom from Insomnia</Link> session if you want a gentle first experience of the style.
                        </p>
                        <p>
                            Each recording is yours to keep forever. That means you can return to it as often as you like, which is often helpful when you want consistent reinforcement rather than a one-time experience.
                        </p>
                    </div>
                    
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/recordings">
                            <Button variant="outline" size="lg" className="rounded-full border-nature-100 text-nature-100 hover:bg-nature-700 w-full sm:w-auto">
                                Browse the Recordings
                            </Button>
                        </Link>
                        <Link to="/recording/freedom-from-insomnia">
                            <Button size="lg" className="rounded-full bg-copper-500 hover:bg-copper-600 text-white w-full sm:w-auto">
                                Start with Free Insomnia Session
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-4">
                            Frequently Asked <span className="italic">Questions</span>
                        </h2>
                    </div>
                    
                    <div className="bg-nature-50 rounded-2xl p-6 md:p-8">
                        {faqs.map((faq, index) => (
                            <FAQItem key={index} question={faq.question} answer={faq.answer} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-nature-50">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="font-heading text-2xl sm:text-3xl font-light text-nature-900 mb-6">
                        Still unsure?
                    </h2>
                    
                    <div className="space-y-4 text-nature-800/80 font-body leading-relaxed text-lg mb-10">
                        <p>
                            If you're still deciding, that's completely fine. The best next step is usually the one that feels easiest to try first. For many people, that means starting with the free insomnia recording and seeing how the style feels before exploring further.
                        </p>
                        <p className="text-nature-900 font-medium">
                            If what you want is a calm, practical, and respectful way to support change, then this may be the right place for you.
                        </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/recording/freedom-from-insomnia">
                            <Button variant="outline" size="lg" className="rounded-full border-nature-300 hover:border-copper-500 w-full sm:w-auto">
                                Try Free Recording
                            </Button>
                        </Link>
                        <Link to="/recordings" data-testid="is-this-for-me-cta">
                            <Button size="lg" className="rounded-full bg-copper-500 hover:bg-copper-600 text-white w-full sm:w-auto">
                                Explore All 45 Recordings
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IsThisForMe;
